import React, { ReactNode } from 'react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ListItemProps } from '@progress/kendo-react-dropdowns';
import styles from '@components-clean/navbar/style.module.scss';
import { CM2Dataset } from '@interfaces/central-monitoring';
import { CommonDataset } from '@pages-clean/shared/types';
import { ExtendedDatasetFieldsWithIcon } from '@utils/cm-observations.service';
import { stringDateWithoutTimeFromUtc } from '@utils/convertor';

export type DatasetUnion = ExtendedDatasetFieldsWithIcon | CM2Dataset | CommonDataset;

export const formatLegacyDatasetDates = (d: ExtendedDatasetFieldsWithIcon) => ({
    ...d,
    dataCutDate: stringDateWithoutTimeFromUtc(d.isCMR ? d?._date : (d?.dataCutDate ?? null), 'DD-MMM-YYYY'),
});

export const formatDatasetDates = (d: CM2Dataset): CM2Dataset => ({
    ...d,
    dataCutDate: stringDateWithoutTimeFromUtc(d?.dataCutDate ?? null, 'DD-MMM-YYYY') as string,
});

export const formatCommonDatasetDates = (d: CommonDataset): CommonDataset => ({
    ...d,
    dataCutDate: stringDateWithoutTimeFromUtc(d?.dataCutDate ?? null, 'DD-MMM-YYYY') as string,
});

export const sortDatasetByDatesDesc = (a: DatasetUnion, b: DatasetUnion) =>
    (b?.dataCutDate ? new Date(b?.dataCutDate).getTime() : 0) -
    (a?.dataCutDate ? new Date(a?.dataCutDate).getTime() : 0);

export const isCMRDataset = (dataset: DatasetUnion) =>
    ('icon' in dataset && dataset.icon === 'clipboard-list') || ('isCMR' in dataset && dataset?.isCMR);

export const getDatasetIcon = (dataset?: DatasetUnion | null): IconProp => {
    if (!dataset) return ['far', 'database'];
    if (isCMRDataset(dataset)) return ['far', 'clipboard'];
    return dataset.active ? ['far', 'database'] : ['far', 'eye-slash'];
};

export const renderDatasetSelectorSpan = (
    element: React.ReactElement<HTMLLIElement | HTMLSpanElement>,
    dataset: DatasetUnion,
) => {
    // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
    // eslint-disable-next-line @eslint-react/no-clone-element
    return React.cloneElement(
        element,
        element.props,
        <span className="k-w-full k-d-flex k-align-items-center k-justify-content-between">
            {element.props.children as ReactNode}
            {dataset != null && (
                <span className="dataset-icon k-d-flex k-justify-content-center">
                    <FontAwesomeIcon size="lg" icon={getDatasetIcon(dataset)} className={styles.iconPrimary} />
                </span>
            )}
        </span>,
    );
};

export const renderDatasetSelectorListItemSpan = (
    element: React.ReactElement<HTMLLIElement | HTMLSpanElement>,
    itemProps: ListItemProps,
) => {
    return renderDatasetSelectorSpan(element, itemProps?.dataItem as DatasetUnion);
};
